import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { BusCompanyMap, VehicleTypeMap } from '../../../core/bookingParameters';

@Component({
  selector: 'app-bus',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  template: `
    <div class="flex flex-col w-full" [formGroup]="busInfo">
      <div class="flex flex-col space-y-6 w-full">
        <!-- Bus Company Dropdown -->
        <div class="flex flex-col sm:flex-row sm:items-end gap-y-2 sm:gap-y-0">
          <div class="flex flex-col sm:pr-6">
            <h2 class="text-base font-semibold leading-7 text-gray-900">
              Busfirma
            </h2>
            <p class="mt-1 text-sm leading-6 text-gray-600">
              Wählen Sie die Busfirma:
            </p>
          </div>
          <div class="w-full sm:w-auto">
            <label for="busCompany" class="sr-only">Bus Company</label>
            <select
              id="buscompany"
              name="buscompany"
              formControlName="busCompany"
              class="w-full sm:w-auto sm:max-w-full rounded-md border border-gray-300 py-2 sm:py-1.5 px-3 text-left text-base font-medium leading-5 text-gray-700 shadow-sm focus:border-columbus-blue-500 focus:outline-none focus:ring-1 focus:ring-columbus-blue-500 sm:text-sm"
            >
              <option>-</option>
              <option *ngFor="let company of busCompanies" [value]="company">
                {{ company }}
              </option>
            </select>
          </div>
        </div>

        <!-- Vehicle Type Dropdowns -->
        <div class="grid grid-cols-2 sm:grid-cols-4 items-end gap-4">
          <div *ngFor="let vehicleType of vehicleTypes">
            <div class="flex flex-col items-center text-center sm:pr-6">
              <h2 class="text-base font-semibold leading-7 text-gray-900">
                {{ vehicleType }}
              </h2>

              <!-- Vehicle Quantity Dropdown -->
              <div class="w-full">
                <label for="vehicle-quantity-{{ vehicleType }}" class="sr-only"
                  >Quantity of vehicles for {{ vehicleType }}</label
                >
                <select
                  id="vehicle-quantity-{{ vehicleType }}"
                  name="vehicle-quantity-{{ vehicleType }}"
                  [formControl]="getVehicleControl(vehicleType)"
                  [value]="selectedVehicleQuantities[vehicleType]"
                  (change)="onVehicleQuantityChange($event, vehicleType)"
                  class="w-full rounded-md border border-gray-300 py-2 sm:py-1.5 px-3 text-center text-base font-medium leading-5 text-gray-700 shadow-sm focus:border-columbus-blue-500 focus:outline-none focus:ring-1 focus:ring-columbus-blue-500 sm:text-sm"
                >
                  <option *ngFor="let qty of vehicleQuantities" [value]="qty">
                    {{ qty }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: ``,
})
export class BusComponent implements OnInit {
  @Input({ required: true }) busInfo!: FormGroup;
  @Output() busCompanyChanged = new EventEmitter<string>();

  fb = inject(FormBuilder);

  // Initialize the bus companies and quantities
  busCompanies = Object.values(BusCompanyMap);
  vehicleQuantities = Array.from({ length: 10 }, (_, i) => i); // Generates [0, 1, 2, ..., 9]

  // Initialize vehicle types from the VehicleTypeMap
  vehicleTypes = Object.values(VehicleTypeMap);

  // Store the selected values for bus company and vehicle quantities
  selectedVehicleQuantities: { [key: string]: number } = {};

  ngOnInit(): void {
    this.vehicleTypes.forEach((vehicleType) => {
      this.selectedVehicleQuantities[vehicleType] = 0;
    });

    const busSelectedArray = this.busSelected.value;
    busSelectedArray.forEach((bus: { type: string; amount: number }) => {
      this.selectedVehicleQuantities[bus.type] = bus.amount;
    });

    this.busInfo.get('busCompany')?.valueChanges.subscribe((company) => {
      this.busCompanyChanged.emit(company);
    });
  }

  // Getter for busSelected FormArray
  get busSelected(): FormArray {
    return this.busInfo.get('busSelected') as FormArray;
  }

  onVehicleQuantityChange(eventValue: Event, vehicleType: string): void {
    const selectedQuantity = parseInt(
      (eventValue.target as HTMLSelectElement).value,
      10,
    );
    const existingIndex = this.busSelected.controls.findIndex(
      (group) => group.get('type')?.value === vehicleType,
    );

    if (selectedQuantity > 0) {
      if (existingIndex === -1) {
        this.busSelected.push(
          this.fb.group({
            type: [vehicleType, Validators.required],
            amount: [
              selectedQuantity,
              [Validators.required, Validators.min(1)],
            ],
          }),
        );
      } else {
        this.busSelected
          .at(existingIndex)
          .patchValue({ amount: selectedQuantity });
      }
    } else if (existingIndex !== -1) {
      this.busSelected.removeAt(existingIndex);
    }

    // Update `selectedVehicleQuantities` for UI sync
    this.selectedVehicleQuantities[vehicleType] = selectedQuantity;
  }

  getVehicleControl(vehicleType: string): FormControl {
    const existingIndex = this.busSelected.controls.findIndex(
      (group) => group.get('type')?.value === vehicleType,
    );

    if (existingIndex !== -1) {
      // Return existing FormControl if found
      return this.busSelected.at(existingIndex).get('amount') as FormControl;
    }

    // Create a new FormControl for this vehicleType if not found
    const newControl = this.fb.control(0, [Validators.min(0)]);
    this.busSelected.push(
      this.fb.group({
        type: [vehicleType, Validators.required],
        amount: newControl,
      }),
    );
    return newControl;
  }
}
